import { formatCheckoutData } from '~/assets/util/formatters'
import Cookies from '~/assets/util/helpers/cookies'

export const state = () => {
	return {
		sport: null,
		camera: null,
		quantity: null,
		accessories: [],
		subscriptionType: '',
		subscription: null,
		installment: null,
		signUp: {
			teamData: {
				name: '',
				sportType: '',
				role: '',
				hearFrom: '',
				location: {
					uri: ''
				},
				gender: '',
				teamType: ''
			}
		},
		userData: {
			name: '',
			email: '',
			phone: '',
			companyName: '',
			firstName: '',
			lastName: ''
		},
		shippingAddress: {
			address: '',
			line1: '',
			city: '',
			country: '',
			postal_code: '',
			state: ''
		},
		billingAddress: {
			address: '',
			line1: '',
			city: '',
			country: '',
			postal_code: '',
			state: ''
		},
		hasUser: false,
		hasTeam: false,
		hasPaymentUser: false,
		isRegisteredUser: false,
		paymentMethod: null,
		order: null,
		checkoutInfo: {
			totalAmount: 0,
			clientSecret: '',
			type: ''
		},
		total: {
			clientTotal: 0,
			backendTotal: 0
		}
	}
}

export const getters = {
	userData (state) {
		return state.userData
	},
	shippingAddress (state) {
		return state.shippingAddress
	},
	billingAddress (state) {
		return state.billingAddress
	},
	subscriptionType (state) {
		return state.subscriptionType
	},
	sportType (state) {
		return state.sport
	},
	role (state) {
		return state.signUp.teamData.role
	},
	isRegisteredUser (state) {
		return state.isRegisteredUser
	},
	camera (state) {
		return state.camera
	},
	quantity (state) {
		return state.quantity
	},
	accessories (state) {
		return state.accessories
	},
	subscription (state) {
		return state.subscription
	},
	paymentMethod (state) {
		return state.paymentMethod
	},
	checkoutInfo (state) {
		return state.checkoutInfo
	},
	total (state) {
		return state.total
	},
	order (state) {
		return state.order
	}
}

export const actions = {
	async createUser ({ commit, dispatch }, userObj) {
		try {
			await dispatch('auth/createAnonymousFirebaseUser', userObj || {}, { root: true })

			if (userObj && userObj.email) {
				commit('setEmail', userObj.email)
			}
			commit('setUserRegistered', true)
		} catch (error) {
			if (error.cause === 'auth/email-already-in-use') {
				throw new Error(this.$i18n.tc(`authorization.${error.cause}`), { cause: error.cause })
			} else {
				throw new Error(error.message)
			}
		}
	},
	async createOrder ({ state, commit, rootGetters }) {
		// Order is entity on BE side for storing and processing all purchase data
		// const { origin, phone, email, firstName, lastName, companyName } = orderObj
		const origin = rootGetters['user/origin']
		const selectedAccessories = []

		state.accessories.forEach((item) => {
			selectedAccessories.push({
				id: item.id,
				quantity: item.qty
			})
		})

		const selectedPeriod = state.subscription.period
		const subscriptions = rootGetters['products/subscriptions']
		const installments = rootGetters['products/installments']
		const selectedCamera = { id: state.camera.id, quantity: state.quantity || 1 }

		let subscriptionsData = []

		const subscription = subscriptions.find((item) => {
			return item.period === selectedPeriod &&
				item.metadata.cameraType === state.camera.metadata.cameraType &&
				item.metadata.type === state.subscriptionType
		})

		if (subscription) {
			// here can be undefined only in case with 2-years service, need to refactor this function to make it more clearly
			subscriptionsData.push(subscription)
		}

		if (rootGetters['user/region'].isUS && selectedPeriod !== 1) {
			const service = installments.find((item) => {
				return item.period === selectedPeriod &&
					item.metadata.cameraType === state.camera.metadata.cameraType &&
					item.metadata.type === state.subscriptionType
			})

			subscriptionsData.push(service)
		}

		subscriptionsData = subscriptionsData.map((subscription) => {
			return {
				id: subscription.id,
				quantity: state.quantity || 1
			}
		})

		const payload = {
			cart: [selectedCamera, ...selectedAccessories, ...subscriptionsData],
			origin
		}

		const response = await this.$axios.post('/api/v1/payment/orders', payload)

		commit('setOrder', response.data.data)
	},
	async updateOrder ({ state, commit, dispatch, rootState }, payload) {
		if (payload.setUserData) {
			payload.data.user.metadata = {
				sportType: state.sport.name,
				accountType: state.subscriptionType,
				campaign: rootState.user.utm,
				role: 'admin',
				teamRole: state.signUp.teamData.role
			}
		}
		const response = await this.$axios.put(`/api/v1/payment/orders/${state.order.id}`, payload.data)

		commit('setOrder', response.data.data)

		if (payload.firstName && payload.lastName) {
			await dispatch('updateFirebaseUserProfile', { displayName: `${payload.firstName} ${payload.lastName}` })
		}
	},
	async getSetupIntentCredentials ({ commit }, payload) {
		try {
			const response = await this.$axios.post('api/v1/payment/payments/setup', { paymentMethods: [payload.type] })
			const checkoutData = response.data?.data

			if (checkoutData) {
				checkoutData.type = payload.type
				commit('setCheckoutInfo', formatCheckoutData(checkoutData))
			}
		} catch (error) {
			throw new Error(error.message)
		}
	},
	async createOrderPayment ({ state, commit, dispatch }, paymentMethodId) {
		const stripe = window.Stripe(process.env.stripeApiKey)
		const couponData = state.coupon
		const payload = {
			paymentMethod: state.paymentMethod.type
		}

		const sscid = Cookies.get('sas_sscid')

		// Share A Sale integration with Payment BE
		// Check comments: https://pixellot.atlassian.net/browse/NPS-14911
		if (sscid) {
			payload.affiliate = { sscid }
		}

		if (couponData && couponData.coupon) {
			payload.promotionCode = couponData.id
		}

		// if (state.paymentMethod?.type === 'klarna') {
		// 	// TODO add here check order 0 amount, coupon will not applicable for 0
		// 	throw new Error(`You cannot use this coupon with ${state.paymentMethod?.type?.toUpperCase()} method`)
		// }

		if (paymentMethodId) {
			payload.paymentMethodId = paymentMethodId
		}

		const response = await this.$axios.post(`/api/v1/payment/orders/${state.order.id}/payments`, payload)
		const paymentMethodInfo = response.data

		if (!paymentMethodInfo) {
			throw new Error('[Payment] Error during creating payment session')
		}

		if (state.paymentMethod.type === 'klarna') {
			await stripe.redirectToCheckout({ sessionId: paymentMethodInfo?.session?.sessionId })
		}
	},
	signUpAnalytics ({ state, dispatch }) {
		const payload = {
			userData: {
				email: state.userData.email,
				phone: state.userData.phone,
				name: state.userData.name,
				address: state.shippingAddress
			}
		}

		payload.teamData = {
			role: state.role
		}
		payload.accountType = state.subscriptionType
		payload.isPurchaseFlow = true

		dispatch('analytics/signUp', payload, { root: true })
	}
}

export const mutations = {
	setSportType (state, sportType) {
		state.sport = sportType
	},
	setEmail (state, email) {
		state.userData.email = email
	},
	setSubscriptionType (state, type) {
		state.subscriptionType = type
	},
	setRole (state, role) {
		state.signUp.teamData.role = role
	},
	setPersonalDetails (state, data) {
		state.userData = data
	},
	setShippingAddress (state, data) {
		state.shippingAddress = data
	},
	setBillingAddress (state, data) {
		state.billingAddress = data
	},
	setUserRegistered (state, payload) {
		state.isRegisteredUser = payload
	},
	setCamera (state, camera) {
		state.camera = camera
	},
	setQuantity (state, qty) {
		state.quantity = qty
	},
	setAccessories (state, accessories) {
		state.accessories = accessories
	},
	setSubscription (state, subscription) {
		state.subscription = subscription
	},
	setPaymentMethod (state, paymentMethod) {
		state.paymentMethod = paymentMethod
	},
	setOrder (state, order) {
		state.order = order
	},
	setCheckoutInfo (state, payment) {
		state.checkoutInfo = payment
	},
	setTotal (state, data) {
		state.total[data.field] = data.value
	}
}
